import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { AppointmentTypes } from 'common';
import { addDays, endOfDay, format, formatDuration, isPast, isToday, startOfDay } from 'date-fns';
import { useEffect, useMemo } from 'react';

import { formatPricing, isValidDateString } from 'application/modules/bookingWizard/common/helpers';
import {
  ISessionLengthAdapter,
  SessionLengthError,
  SessionLengthForm,
} from 'application/modules/bookingWizard/useCases/hooks/useCaseChooseSessionLength';
import mboAPI from 'infrastructure/redux/adapters/mboAPI/mboApi';
import { selectAccessToken } from 'infrastructure/redux/slices/auth.selector';
import {
  selectLocation,
  selectPartySize,
  selectReservationDate,
} from 'infrastructure/redux/slices/bookingWizard.selector';
import {
  setAddons,
  setAppointments,
  setPartySize,
  setReservationDate,
  setStartTimeAndSessionLength,
} from 'infrastructure/redux/slices/bookingWizard.slice';
import { setTotalAmountToPay } from 'infrastructure/redux/slices/payment.slice';
import { selectUserSummary } from 'infrastructure/redux/slices/user.selector';
import { setUserSummary } from 'infrastructure/redux/slices/user.slice';
import { useAppDispatch, useAppSelector } from 'infrastructure/redux/store/hooks';

function getError(
  error: FetchBaseQueryError | SerializedError | undefined,
): SessionLengthError | undefined {
  if (!error) {
    return undefined;
  }
  return SessionLengthError.Unknown;
}

type Availability = {
  staffLetter: string;
  staffId: number;
  durations: {
    duration: number;
    cost: number;
    alternativeLocations?: Array<{ locationId: string; locationName: string }>;
  }[];
};

function getAvailableStaffIds(
  availabilities: Availability[],
  duration: number,
): {
  staffIds: number[];
  cost: number;
  alternativeLocations?: Array<{ locationId: string; locationName: string }>;
} {
  const availableStaffIds: number[] = [];
  let cost = 0;
  let alternativeLocations: Array<{ locationId: string; locationName: string }> | undefined;

  for (const availability of availabilities) {
    const matchDuration = availability.durations.find((d) => d.duration === duration);
    if (matchDuration) {
      availableStaffIds.push(availability.staffId);
      cost = matchDuration.cost;
      // Capture alternative locations from the first match
      if (!alternativeLocations && matchDuration.alternativeLocations) {
        alternativeLocations = matchDuration.alternativeLocations;
      }
    }
    // We only need 2 first available staff ids
    if (availableStaffIds.length === 2) {
      break;
    }
  }
  return { staffIds: availableStaffIds, cost, alternativeLocations };
}

const useSessionLength: ISessionLengthAdapter = () => {
  const selectedLocationId = useAppSelector(selectLocation);
  const selectedPartySize = useAppSelector(selectPartySize);
  const reservationDate = useAppSelector(selectReservationDate);
  const user = useAppSelector(selectUserSummary);
  const accessToken = useAppSelector(selectAccessToken);
  const dispatch = useAppDispatch();
  const isReservationDateToday = reservationDate && isToday(new Date(reservationDate));

  const endDate = reservationDate && endOfDay(new Date(reservationDate));
  const startDate =
    reservationDate &&
    (isReservationDateToday ? new Date(reservationDate) : startOfDay(new Date(reservationDate)));

  const [getPrices, result] = mboAPI.useGetAppointmentPricingMutation();
  const [getWholeDayPrices, wholeDayPrices] = mboAPI.useGetHourlySimulatorPricingMutation({
    fixedCacheKey: 'SIM_RATES',
  });
  const [getUser] = mboAPI.useLazyGetUserDataQuery();

  const isDateValid = useMemo(() => {
    if (!reservationDate || Number.isNaN(new Date(reservationDate).getTime())) return;

    return isValidDateString(reservationDate) && !isPast(addDays(new Date(reservationDate), 1));
  }, [reservationDate]);

  const [
    getAvailabilities,
    { data, isLoading, error: appointmentAvailabilityError, isFetching },
    lastAvailabilitiesArg,
  ] = mboAPI.useLazyGetAvailableSimulatorRentalAppointmentsQuery();
  const handleRefetch = () => {
    if (data) {
      getAvailabilities(lastAvailabilitiesArg.lastArg);
    }
  };

  const handleSaveAppointmentDataToStore = (values: SessionLengthForm) => {
    const pricings = formatPricing(result.data);
    dispatch(
      setStartTimeAndSessionLength({
        startTime: values.startDateTime,
        endTime: values.endDateTime,
        sessionLength: values.sessionLength,
      }),
    );
    dispatch(setAppointments({ appointments: pricings.appointments }));
    const total = pricings.appointments?.reduce(
      (total, item) => Number(total) + Number(item.price),
      0,
    );
    dispatch(setAddons(pricings.addOn));
    dispatch(setTotalAmountToPay({ total }));
  };

  const handleSavePartySizeToStore = (selectedPartySize: string) => {
    dispatch(setPartySize({ partySize: selectedPartySize }));
  };

  const handleSaveReservationDateToStore = (selectedReservationDate: Date) => {
    dispatch(setReservationDate({ reservationDate: selectedReservationDate }));
  };

  const availableTimeSlots = useMemo(() => {
    const reportedScheduleStep = data?.[0]?.scheduleStep;
    const scheduleStep =
      reportedScheduleStep && reportedScheduleStep > 0 ? reportedScheduleStep : 30;
    const MAX_DURATION_MINUTES = 240;
    const buttonAvailabilities: number[] = [];
    for (let d = scheduleStep; d <= MAX_DURATION_MINUTES; d += scheduleStep) {
      buttonAvailabilities.push(d);
    }

    return data
      ?.map((item) => {
        const availabilities = item.availabilities;
        const sectionTitle = item.time.toString();
        const buttonData = {
          sectionTitle: format(new Date(sectionTitle.slice(0, -1)), 'hh:mm aa'),
          sectionValue: `${item.time}`,
          buttonList: buttonAvailabilities.map((button) => ({
            value: button,
            title: formatDuration({ hours: button / 60 }, { format: ['hours'], zero: true }),
            additionalValue: '',
            isDisabled: true,
            availableForSmallEvents:
              getAvailableStaffIds(availabilities, button).staffIds.length > 1,
            price: 0,
          })),
        };

        buttonData.buttonList.forEach((button) => {
          const availability = getAvailableStaffIds(availabilities, button.value);
          if (availability.staffIds.length > 0) {
            button.additionalValue = availability.staffIds.join(',');
            button.isDisabled = false;
            button.price = availability.cost;
          }
          // Always add alternativeLocations if they exist (even for disabled buttons)
          if (availability.alternativeLocations) {
            (button as any).alternativeLocations = availability.alternativeLocations;
          }
        });

        return buttonData;
      })
      .filter((slot) => {
        // Keep slot if at least one button is enabled OR if any button has alternative locations
        const hasEnabledButton = slot.buttonList.some((button) => !button.isDisabled);
        const hasAlternatives = slot.buttonList.some(
          (button) => (button as any).alternativeLocations?.length > 0,
        );
        return hasEnabledButton || hasAlternatives;
      });
  }, [data]);

  const handleGetPricing = (values: AppointmentTypes.AppointmentPricingRequest) =>
    getPrices({
      ...values,
      email: user?.email,
    });

  const handleGetWholeDayPricing = (values: AppointmentTypes.HourlySimulatorPricingRequestDto) =>
    getWholeDayPrices({
      locationId: values.locationId,
      email: values.email,
    });

  const handleGetUserData = async (newLocationId: string) => {
    if (accessToken) {
      const payload = await getUser(newLocationId).unwrap();
      dispatch(setUserSummary(payload));
    }
  };

  useEffect(() => {
    if (
      selectedLocationId &&
      startDate &&
      endDate &&
      selectedPartySize &&
      isDateValid &&
      process.env.VITE_IS_WEB &&
      selectedPartySize !== '13+' // Exclude 13+ party size from availability check
    ) {
      getAvailabilities({
        locationId: selectedLocationId || '',
        startDateTime: startDate && isDateValid ? format(startDate, 'yyyy-MM-dd') : undefined,
        endDateTime: endDate && isDateValid ? format(endDate, 'yyyy-MM-dd') : undefined,
        email: user?.email ?? undefined,
        partySize: selectedPartySize,
      });
    }
  }, [
    selectedLocationId,
    startDate?.toString(),
    endDate?.toString(),
    isDateValid,
    selectedPartySize,
    user?.email,
    user?.member,
  ]);

  return {
    error: getError(result.error),
    result: result.data,
    isSuccess: result.isSuccess,
    inProgress: result.isLoading,
    areAppointmentsLoading: isLoading || isFetching,
    appointmentAvailabilityError: getError(appointmentAvailabilityError),
    availableTimeSlots,
    getPricing: handleGetPricing,
    getAvailabilities,
    refetchAvailabilities: handleRefetch,
    getWholeDayPricing: handleGetWholeDayPricing,
    wholeDayPrices: wholeDayPrices.data,
    userData: user,
    handleGetUserData,
    areWholePricesLoading: wholeDayPrices.isLoading,
    handleSaveAppointmentDataToStore,
    handleSavePartySizeToStore,
    handleSaveReservationDateToStore,
  };
};

export default useSessionLength;
